import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
// Fix Leaflet default marker icon (use CDN URLs to avoid bundler issues)
L.Marker.prototype.options.icon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
function MapUpdater({
  center
}: {
  center: [number, number];
}) {
  const map = useMap();
  map.setView(center, map.getZoom());
  return null;
}
export function ForestCompliance() {
  const [activeTab, setActiveTab] = useState<'cases' | 'file' | 'blacklist'>('cases');
  const [currentStep, setCurrentStep] = useState(0);
  // Form State
  const [formData, setFormData] = useState({
    offenderSearch: '',
    offenderName: '',
    offenderDescription: '',
    offenseType: '',
    date: '',
    time: '',
    latitude: '21.9497',
    longitude: '89.1833',
    description: '',
    fineAmount: '',
    suspendPermit: false,
    blacklist: false,
    notes: ''
  });
  const [casesList, setCasesList] = useState([{
    caseId: 'CASE-2024-001',
    offender: 'Unknown Person',
    offense: 'Illegal Entry',
    date: '2024-03-28',
    latitude: 21.9497,
    longitude: 89.1833,
    status: 'open' as const,
    severity: 'High'
  }, {
    caseId: 'CASE-2024-002',
    offender: 'Habib Mollah',
    offense: 'Poaching',
    date: '2024-03-25',
    latitude: 21.8285,
    longitude: 89.5862,
    status: 'resolved' as const,
    severity: 'Critical'
  }, {
    caseId: 'CASE-2024-003',
    offender: 'Nur Islam',
    offense: 'Overharvesting',
    date: '2024-03-20',
    latitude: 22.0174,
    longitude: 89.75,
    status: 'escalated' as const,
    severity: 'Medium'
  }, {
    caseId: 'CASE-2024-004',
    offender: 'Kamal Uddin',
    offense: 'Unauthorized Vessel',
    date: '2024-03-15',
    latitude: 21.8,
    longitude: 89.55,
    status: 'open' as const,
    severity: 'Low'
  }]);
  const [blacklistData, setBlacklistData] = useState([{
    name: 'Habib Mollah',
    nid: '7890123456789',
    reason: 'Repeated Poaching Offenses',
    blacklistedDate: '2024-03-25',
    duration: '2 years',
    caseRef: 'CASE-2024-002'
  }, {
    name: 'Karim Talukdar',
    nid: '8901234567890',
    reason: 'Illegal Timber Trade',
    blacklistedDate: '2024-02-10',
    duration: '5 years',
    caseRef: 'CASE-2024-015'
  }]);
  const caseColumns = [{
    key: 'caseId',
    label: 'Case ID'
  }, {
    key: 'offender',
    label: 'Offender Name'
  }, {
    key: 'offense',
    label: 'Offense Type'
  }, {
    key: 'date',
    label: 'Date'
  }, {
    key: 'latitude',
    label: 'Latitude'
  }, {
    key: 'longitude',
    label: 'Longitude'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-883" data-mp="1sm5yujyl94kb" />
  }, {
    key: 'severity',
    label: 'Severity'
  }];
  const blacklistColumns = [{
    key: 'name',
    label: 'Name'
  }, {
    key: 'nid',
    label: 'NID'
  }, {
    key: 'reason',
    label: 'Reason'
  }, {
    key: 'blacklistedDate',
    label: 'Blacklisted Date'
  }, {
    key: 'duration',
    label: 'Duration'
  }, {
    key: 'caseRef',
    label: 'Case Reference'
  }];
  const caseFilters = [{
    key: 'offense',
    label: 'Offense Type',
    options: ['Illegal Entry', 'Poaching', 'Overharvesting', 'Unauthorized Vessel']
  }, {
    key: 'status',
    label: 'Status',
    options: ['Open', 'Resolved', 'Escalated']
  }, {
    key: 'severity',
    label: 'Severity',
    options: ['Low', 'Medium', 'High', 'Critical']
  }];
  const wizardSteps = [{
    title: 'Offender Info',
    description: 'Identify the offender'
  }, {
    title: 'Offense Details',
    description: 'Enter offense information'
  }, {
    title: 'Action Taken',
    description: 'Record actions and penalties'
  }, {
    title: 'Review & Submit',
    description: 'Review all information before submitting'
  }];
  return <div className="space-y-6" data-id="element-884" data-mp="lroxc0g1dn3f">
      <div data-id="element-885" data-mp="lsqbidscmppd">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-886" data-mp="lt00pz5kksu0">
          Forest Case & Compliance
        </h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-887" data-mp="2siof2xtx797r">
          Track offenses and manage blacklist
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-888" data-mp="1310yxrnuavpr">
        <div className="flex gap-4" data-id="element-889" data-mp="132c15n1mzver">
          <button onClick={() => setActiveTab('cases')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'cases' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-890" data-mp="133dexza1ew06">
            Case Registry
          </button>
          <button onClick={() => setActiveTab('file')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'file' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-891" data-mp="ka03hdlxhiay">
            File New Case
          </button>
          <button onClick={() => setActiveTab('blacklist')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'blacklist' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-892" data-mp="fchtrl036z1w">
            Blacklist Registry
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'cases' && <DataTable columns={caseColumns} data={casesList} filters={caseFilters} onDataChange={setCasesList} searchPlaceholder="Search cases..." data-id="element-893" data-mp="237wbis1nvx15" />}

      {activeTab === 'blacklist' && <DataTable columns={blacklistColumns} data={blacklistData} onDataChange={setBlacklistData} searchPlaceholder="Search blacklisted individuals..." data-id="element-894" data-mp="2qzbiiy4dv2iq" />}

      {activeTab === 'file' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      if (!formData.offenseType || !formData.date) {
        alert('Please fill in required fields.');
        return;
      }
      const newCase = {
        caseId: `CASE-2024-${String(casesList.length + 1).padStart(3, '0')}`,
        offender: formData.offenderName || 'Unknown Person',
        offense: formData.offenseType,
        date: formData.date,
        latitude: parseFloat(formData.latitude) || 21.9497,
        longitude: parseFloat(formData.longitude) || 89.1833,
        status: 'open' as const,
        severity: 'Medium'
      };
      setCasesList([newCase, ...casesList]);
      alert('Case filed successfully!');
      setCurrentStep(0);
      setActiveTab('cases');
      // Reset form
      setFormData({
        offenderSearch: '',
        offenderName: '',
        offenderDescription: '',
        offenseType: '',
        date: '',
        time: '',
        latitude: '21.9497',
        longitude: '89.1833',
        description: '',
        fineAmount: '',
        suspendPermit: false,
        blacklist: false,
        notes: ''
      });
    }} data-id="element-895" data-mp="2h75rprpvar54">
          {currentStep === 0 && <div className="space-y-4" data-id="element-896" data-mp="141bw9ciptcr7">
              <div data-id="element-897" data-mp="144g7gtull8rd">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-898" data-mp="1i1moaivrgxdy">
                  Search Beneficiary
                </label>
                <input type="text" value={formData.offenderSearch} onChange={e => setFormData({
            ...formData,
            offenderSearch: e.target.value
          })} placeholder="Enter NID or Name (leave empty if unknown)" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-899" data-mp="1d438hejf0cjw" />
              </div>
              <div className="text-center text-sm text-gray-600 py-2" data-id="element-900" data-mp="1nwjf9wti539">
                OR Enter Unknown Person Details
              </div>
              <div className="space-y-4" data-id="element-901" data-mp="2da6rf9n79q7n">
                <div data-id="element-902" data-mp="2d95e0yipormr">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-903" data-mp="2i1gi8dkhizl4">
                    Name (if known)
                  </label>
                  <input type="text" value={formData.offenderName} onChange={e => setFormData({
              ...formData,
              offenderName: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-904" data-mp="24bv5vm16xim2" />
                </div>
                <div data-id="element-905" data-mp="2rybcgk9q6dtl">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-906" data-mp="2s1z5zjf6708b">
                    Description
                  </label>
                  <textarea rows={3} value={formData.offenderDescription} onChange={e => setFormData({
              ...formData,
              offenderDescription: e.target.value
            })} placeholder="Physical description, clothing, etc." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-907" data-mp="1gl8y7vv3m0cl"></textarea>
                </div>
              </div>
            </div>}
          {currentStep === 1 && <div className="space-y-4" data-id="element-908" data-mp="16rko6fqy6eyo">
              <div data-id="element-909" data-mp="16smd9o4st1ze">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-910" data-mp="16t5koawto7z6">
                  Offense Type *
                </label>
                <select value={formData.offenseType} onChange={e => setFormData({
            ...formData,
            offenseType: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-911" data-mp="315lxxvo1cmdj">
                  <option value="" data-id="element-912" data-mp="147oew8ybpju5">Select Offense</option>
                  <option value="Illegal Entry" data-id="element-913" data-mp="14998nwctcyqy">Illegal Entry</option>
                  <option value="Poaching" data-id="element-914" data-mp="zfo8bxnqzsol">Poaching</option>
                  <option value="Overharvesting" data-id="element-915" data-mp="1d7kvroeh1een">Overharvesting</option>
                  <option value="Unauthorized Vessel" data-id="element-916" data-mp="1d9nuqxxlqhvi">
                    Unauthorized Vessel
                  </option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-917" data-mp="1n4pltzbftu0q">
                <div data-id="element-918" data-mp="1i7787vmh6qme">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-919" data-mp="1i6o1lb29zono">
                    Date *
                  </label>
                  <input type="date" value={formData.date} onChange={e => setFormData({
              ...formData,
              date: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-920" data-mp="3oxng7zkv4uns" />
                </div>
                <div data-id="element-921" data-mp="rhmg5cead0ls">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-922" data-mp="ri4jsu3a2h3q">
                    Time *
                  </label>
                  <input type="time" value={formData.time} onChange={e => setFormData({
              ...formData,
              time: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-923" data-mp="10h9mmclvgwlp" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-924" data-mp="34n9ozo18nel0">
                <div data-id="element-925" data-mp="34ouirbfqatht">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-926" data-mp="34l6p8caaa733">
                    Latitude *
                  </label>
                  <input type="number" step="0.000001" value={formData.latitude} onChange={e => setFormData({
              ...formData,
              latitude: e.target.value
            })} placeholder="e.g. 21.9497" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-927" data-mp="2uok5xrxlvo36" />
                </div>
                <div data-id="element-928" data-mp="9z4k7bd8upke">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-929" data-mp="9ze8muin4onz">
                    Longitude *
                  </label>
                  <input type="number" step="0.000001" value={formData.longitude} onChange={e => setFormData({
              ...formData,
              longitude: e.target.value
            })} placeholder="e.g. 89.1833" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-930" data-mp="573anx7gncy2" />
                </div>
              </div>
              <div className="h-[300px] w-full rounded-lg overflow-hidden border border-gray-300 z-0 relative" data-id="element-931" data-mp="dffoljoooxs0">
                <MapContainer center={[parseFloat(formData.latitude) || 21.9497, parseFloat(formData.longitude) || 89.1833]} zoom={10} style={{
            height: '100%',
            width: '100%'
          }} data-id="element-932" data-mp="3uue7xh1m0or1">
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' data-id="element-933" data-mp="2gvkqii140dpa" />
                  <MapUpdater center={[parseFloat(formData.latitude) || 21.9497, parseFloat(formData.longitude) || 89.1833]} data-id="element-934" data-mp="2lnlznv2zdob3" />
                  <Marker position={[parseFloat(formData.latitude) || 21.9497, parseFloat(formData.longitude) || 89.1833]} data-id="element-935" data-mp="1szq8f5w6kolp" />
                </MapContainer>
              </div>
              <div data-id="element-936" data-mp="3idl87pj4smto">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-937" data-mp="3id3bkrsk46l8">
                  Description *
                </label>
                <textarea rows={4} value={formData.description} onChange={e => setFormData({
            ...formData,
            description: e.target.value
          })} placeholder="Detailed description of the offense" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-938" data-mp="3ec01klx6x4rx"></textarea>
              </div>
              <div data-id="element-939" data-mp="1x9fraf61vg9p">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-940" data-mp="1xb0jvz8t0p4x">
                  Evidence Upload
                </label>
                <input type="file" multiple accept="image/*,video/*" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-941" data-mp="219g3ru9l9he4" />
              </div>
            </div>}
          {currentStep === 2 && <div className="space-y-4" data-id="element-942" data-mp="161e70syj0j62">
              <div data-id="element-943" data-mp="160crab6kf8i0">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-944" data-mp="113cktz5t03qd">
                  Fine Amount (BDT)
                </label>
                <input type="number" value={formData.fineAmount} onChange={e => setFormData({
            ...formData,
            fineAmount: e.target.value
          })} placeholder="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-945" data-mp="x0fzw1ssjvj9" />
              </div>
              <div className="space-y-2" data-id="element-946" data-mp="9dhk76chwrme">
                <label className="flex items-center gap-2" data-id="element-947" data-mp="9adcw03qhfri">
                  <input type="checkbox" checked={formData.suspendPermit} onChange={e => setFormData({
              ...formData,
              suspendPermit: e.target.checked
            })} className="w-4 h-4 text-[#1B5E20] rounded" data-id="element-948" data-mp="4ck552ms8dp4" />
                  <span className="text-sm font-medium text-gray-700" data-id="element-949" data-mp="1e9qouqiveo5a">
                    Suspend Permit
                  </span>
                </label>
                <label className="flex items-center gap-2" data-id="element-950" data-mp="14jp2q4a2vft0">
                  <input type="checkbox" checked={formData.blacklist} onChange={e => setFormData({
              ...formData,
              blacklist: e.target.checked
            })} className="w-4 h-4 text-[#1B5E20] rounded" data-id="element-951" data-mp="14idw7wp8zty7" />
                  <span className="text-sm font-medium text-gray-700" data-id="element-952" data-mp="lnp62g65no33">
                    Add to Blacklist
                  </span>
                </label>
              </div>
              <div data-id="element-953" data-mp="6yw5e45e2q04">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-954" data-mp="70gzjsnsk6xg">
                  Additional Notes
                </label>
                <textarea rows={4} value={formData.notes} onChange={e => setFormData({
            ...formData,
            notes: e.target.value
          })} placeholder="Any additional actions taken or notes" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-955" data-mp="gx3jmf8amtyf"></textarea>
              </div>
            </div>}
          {currentStep === 3 && <div className="space-y-6" data-id="element-956" data-mp="18n82g9gen8xj">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-957" data-mp="18o9by9hrvnd5">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-958" data-mp="14n7cpsnuvbb6">
                  Offender Information
                </h4>
                <div className="text-sm" data-id="element-959" data-mp="1h3kytvlz8rlh">
                  <div data-id="element-960" data-mp="1c61h2lq2lwos">
                    <span className="text-gray-600" data-id="element-961" data-mp="1c6ugt2qm3lxf">Name:</span>{' '}
                    <span className="font-medium" data-id="element-962" data-mp="1q5u5gae4k0tl">
                      {formData.offenderName || 'Unknown Person'}
                    </span>
                  </div>
                  <div data-id="element-963" data-mp="1l7iwe1vnlucf">
                    <span className="text-gray-600" data-id="element-964" data-mp="1l4wn9xt476s3">Description:</span>{' '}
                    <span className="font-medium" data-id="element-965" data-mp="xhesruigtulf">
                      {formData.offenderDescription || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-966" data-mp="1791am9pb6rx5">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-967" data-mp="12f6u1f502t2a">
                  Offense Details
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-968" data-mp="eoug9nn2q23s">
                  <div data-id="element-969" data-mp="9scduuf7ufum">
                    <span className="text-gray-600" data-id="element-970" data-mp="9r1b8xxibe53">Type:</span>{' '}
                    <span className="font-medium" data-id="element-971" data-mp="2lenzqs2ch6uk">
                      {formData.offenseType || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-972" data-mp="2q7h2jae96540">
                    <span className="text-gray-600" data-id="element-973" data-mp="2cerhb86jt47y">Date:</span>{' '}
                    <span className="font-medium" data-id="element-974" data-mp="2ccesqc6abvlx">
                      {formData.date || 'Not provided'} {formData.time}
                    </span>
                  </div>
                  <div data-id="element-975" data-mp="353f08x3trhic">
                    <span className="text-gray-600" data-id="element-976" data-mp="3512jgn9szd6v">Latitude:</span>{' '}
                    <span className="font-medium" data-id="element-977" data-mp="393ywlygkqh3f">
                      {formData.latitude || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-978" data-mp="3030zm04jz9uc">
                    <span className="text-gray-600" data-id="element-979" data-mp="302is27cfx974">Longitude:</span>{' '}
                    <span className="font-medium" data-id="element-980" data-mp="1jpatkntvibi2">
                      {formData.longitude || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-981" data-mp="14msm2l4wjmr6">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-982" data-mp="14oddw2zu0rlc">
                  Action Taken
                </h4>
                <div className="text-sm" data-id="element-983" data-mp="vtz24aeifz6z">
                  <div data-id="element-984" data-mp="vsxopza0v0m3">
                    <span className="text-gray-600" data-id="element-985" data-mp="vs4getvf031u">Fine:</span>{' '}
                    <span className="font-medium" data-id="element-986" data-mp="quco3yquyex4">
                      {formData.fineAmount ? `${formData.fineAmount} BDT` : 'None'}
                    </span>
                  </div>
                  <div data-id="element-987" data-mp="gz2o1gk0shlz">
                    <span className="text-gray-600" data-id="element-988" data-mp="cv38p1ah7ou7">Permit Suspended:</span>{' '}
                    <span className="font-medium" data-id="element-989" data-mp="cx7ly9u9m075">
                      {formData.suspendPermit ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div data-id="element-990" data-mp="2782s75hz936r">
                    <span className="text-gray-600" data-id="element-991" data-mp="277b8ot1nmvwu">Blacklisted:</span>{' '}
                    <span className="font-medium" data-id="element-992" data-mp="22aubl9ch977m">
                      {formData.blacklist ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </WizardForm>}
    </div>;
}